import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

interface BulkWorkflowScreenProps {
  className?: string;
  children?: React.ReactNode;
}

/*
This module provides stateless components to help build a screen used for a bulk workflow.
An example structure would look as follows:

<BulkWorkflowScreen>
 <Page>
   <Nav>
    <NavItem>item 1</NavItem>
    <NavItem>item 2</NavItem>
    <NavItem>item 3</NavItem>
   </Nav>
   <Main>
     <Pane>
        <Steps>
          <Step></Step>
          <Step></Step>
          <Step></Step>
        </Steps>
     </Pane>
     <Pane></Pane>
   </Main>
 </Page>
</BulkWorkflowScreen>
*/

export function BulkWorkflowScreen(props: BulkWorkflowScreenProps): JSX.Element {
  return <div {...props} className={classNames('bulkWorkflow-screen', props.className)} />;
}

interface StepsProps {
  children?: React.ReactNode;
}

export function Steps({children}: StepsProps): JSX.Element {
  return <ol className="bulkWorkflow-steps">{children}</ol>;
}

interface StepProps {
  text?: React.ReactNode;
  children?: React.ReactNode;
}

export function Step({text, children}: StepProps): JSX.Element {
  return (
    <li className="bulkWorkflow-step" {...getTestProps({component: 'Step', element: 'root'})}>
      <div className="bulkWorkflow-step-text">{text}</div>
      <div className="bulkWorkflow-step-body">{children}</div>
    </li>
  );
}

interface NavItemProps {
  hasErrors?: boolean;
  id?: number;
  isActive?: boolean;
  isPlaceholder?: boolean;
  onClick: (e: ElementMouseEvent, id?: number) => void;
  onDelete?: (e: ElementMouseEvent, id?: number) => void;
}

export class NavItem extends React.Component<NavItemProps> {
  _handleClick = (e: ElementMouseEvent): void => {
    this.props.onClick(e, this.props.id);
  };

  _handleTrashIconClick = (e: ElementMouseEvent): void => {
    this.props.onDelete!(e, this.props.id);
  };

  render(): JSX.Element {
    return (
      <div
        onClick={this._handleClick}
        className={classNames(
          'bulkWorkflow-nav-item',
          {
            'is-active': this.props.isActive,
            'is-placeholder': this.props.isPlaceholder,
          },
          css(styles.navItem)
        )}
      >
        <div className="u-flex-grow">{this.props.children}</div>
        <div className="u-flex-none">
          <BenchlingIcon
            icon="exclamation"
            className={css(this.props.hasErrors ? styles.errorIcon : styles.errorIconInvisible)}
          />
          <div className={css(styles.navButtonWrapper)}>
            {this.props.onDelete && (
              <BenchlingIcon
                icon="format-delete"
                className={css(styles.removeBtn)}
                onClick={this._handleTrashIconClick}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

interface NavProps {
  header?: React.ReactNode;
  headerOptions?: React.ReactNode;
  children?: React.ReactNode;
  banner?: React.ReactNode;
}

export function Nav({children, header, headerOptions, banner}: NavProps): JSX.Element {
  return (
    <div className="bulkWorkflow-nav">
      <div className={css(styles.headerWrapper)}>
        <span className="bulkWorkflow-nav-header">{header}</span>
        {headerOptions && <span className={css(styles.headerOptions)}>{headerOptions}</span>}
      </div>
      <div className="bulkWorkflow-nav-scrollPane">
        {banner}
        <div className="bulkWorkflow-nav-items">
          <div className="bulkWorkflow-nav-items-line" />
          {children}
        </div>
      </div>
    </div>
  );
}

interface PageProps {
  className?: string;
  children?: React.ReactNode;
}

export function Page(props: PageProps): JSX.Element {
  return <div {...props} className={classNames('bulkWorkflow-page', props.className)} />;
}

interface MainProps {
  className?: string;
  children?: React.ReactNode;
}

export function Main(props: MainProps): JSX.Element {
  return <div {...props} className={classNames('bulkWorkflow-main', props.className)} />;
}

interface PaneProps {
  className?: string;
  header?: React.ReactNode;
  children?: React.ReactNode;
}

export function Pane(props: PaneProps): JSX.Element {
  return (
    // eslint-disable-next-line no-restricted-syntax
    <div {..._.omit(props, 'header')} className={classNames('bulkWorkflow-pane', props.className)}>
      {props.header && <div className="bulkWorkflow-pane-header">{props.header}</div>}
      <div className="bulkWorkflow-pane-content">{props.children}</div>
    </div>
  );
}

interface ErrorMessagesProps {
  className?: string;
  children?: React.ReactNode;
}

export function ErrorMessages(props: ErrorMessagesProps): JSX.Element {
  return <div {...props} className={classNames('bulkWorkflow-errors-and-warnings', props.className)} />;
}

interface ErrorMessageProps {
  children?: React.ReactNode;
}

export function ErrorMessage(props: ErrorMessageProps): JSX.Element {
  return (
    <div className="bulkWorkflow-error">
      <BenchlingIcon icon="exclamation" className="bulkWorkflow-error-icon" />
      <span className="bulkWorkflow-error-msg">{props.children}</span>
    </div>
  );
}

interface WarningsProps {
  className?: string;
  children?: React.ReactNode;
}

export function Warnings(props: WarningsProps): JSX.Element {
  return <div {...props} className={classNames('bulkWorkflow-errors-and-warnings', props.className)} />;
}

interface WarningProps {
  children?: React.ReactNode;
}

export function Warning(props: WarningProps): JSX.Element {
  return (
    <div>
      <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
      <span className={css(styles.warningMessage)}>{props.children}</span>
    </div>
  );
}

interface SuccessMessageProps {
  children?: React.ReactNode;
}

export function SuccessMessage(props: SuccessMessageProps): JSX.Element {
  return (
    <div>
      <BenchlingIcon icon="check" className={css(styles.successIcon)} />
      <span className={css(styles.successMessage)}>{props.children}</span>
    </div>
  );
}

interface LabelProps {
  allCaps: boolean;
  className?: string;
  children: React.ReactNode;
}

export function Label(props: LabelProps): JSX.Element {
  return (
    <label
      // eslint-disable-next-line no-restricted-syntax
      {..._.omit(props, 'allCaps')}
      className={classNames({'bulkWorkflow-label--allCaps': props.allCaps}, props.className)}
    />
  );
}

Label.defaultProps = {
  allCaps: false,
};

const styles = StyleSheet.create({
  headerWrapper: {
    display: 'flex',
  },
  headerOptions: {
    marginLeft: 20,
  },
  navItem: {
    ':hover': {
      border: `1px solid ${Colors.BLUE_LIGHT}`,
    },
  },
  errorIcon: {
    color: Colors.RED_DARK,
  },
  errorIconInvisible: {
    visibility: 'hidden',
  },
  navButtonWrapper: {
    margin: '15px 0px',
  },
  removeBtn: {
    color: Colors.GRAY_DARK,
    ':hover': {
      color: Colors.RED_DARK,
    },
  },
  warningIcon: {
    color: Colors.ORANGE,
    marginRight: '4px',
    verticalAlign: 'middle',
  },
  warningMessage: {
    verticalAlign: 'middle',
  },
  successIcon: {
    color: Colors.GREEN,
    marginRight: '4px',
    verticalAlign: 'middle',
  },
  successMessage: {
    verticalAlign: 'middle',
  },
});
